/**
 * Episode-of-care scoped appointment sequence + prev/next helpers.
 */
import type { Appointment } from '@medplum/fhirtypes';
import type { MedplumClient } from '@medplum/core';
import { getApptEocContextRef } from '@unify/fhir-model/resources/appointment';
import type { LinkedAppointmentInfo } from '../types';
import { formatLinkedDate, formatLinkedTime } from './dateFormatters';

export interface EpisodeLinkedAppointmentsResult {
  sequenceNumber: number | null;
  totalInEpisode: number;
  previous?: LinkedAppointmentInfo;
  next?: LinkedAppointmentInfo;
  appointmentsLeft: number | null;
}

function appointmentEocId(appt: Appointment): string | undefined {
  const ref = getApptEocContextRef(appt)?.reference;
  if (!ref?.startsWith('EpisodeOfCare/')) return undefined;
  return ref.slice('EpisodeOfCare/'.length).trim() || undefined;
}

function toLinkedInfo(appt: Appointment, sequenceNumber: number): LinkedAppointmentInfo {
  return {
    id: appt.id,
    date: formatLinkedDate(appt.start),
    time: formatLinkedTime(appt.start, appt.end),
    status: appt.status,
    sequenceNumber,
  };
}

function startMs(appt: Appointment): number {
  if (!appt.start) return Number.POSITIVE_INFINITY;
  const ms = Date.parse(appt.start);
  return Number.isFinite(ms) ? ms : Number.POSITIVE_INFINITY;
}

/**
 * Pure: filter appointments to an episode, sort by start, resolve sequence + neighbors.
 */
export function resolveEpisodeLinkedAppointments(
  appointments: Appointment[],
  currentAppointmentId: string,
  episodeOfCareId: string | undefined,
): EpisodeLinkedAppointmentsResult {
  const empty: EpisodeLinkedAppointmentsResult = {
    sequenceNumber: null,
    totalInEpisode: 0,
    appointmentsLeft: null,
  };

  if (!episodeOfCareId) {
    return empty;
  }

  const inEpisode = appointments
    .filter((a) => a.id && appointmentEocId(a) === episodeOfCareId)
    .sort((a, b) => {
      const diff = startMs(a) - startMs(b);
      if (diff !== 0) return diff;
      return (a.id ?? '').localeCompare(b.id ?? '');
    });

  const index = inEpisode.findIndex((a) => a.id === currentAppointmentId);
  if (index < 0) {
    return {
      sequenceNumber: null,
      totalInEpisode: inEpisode.length,
      appointmentsLeft: null,
    };
  }

  const previous = index > 0 ? toLinkedInfo(inEpisode[index - 1], index) : undefined;
  const next =
    index < inEpisode.length - 1 ? toLinkedInfo(inEpisode[index + 1], index + 2) : undefined;

  return {
    sequenceNumber: index + 1,
    totalInEpisode: inEpisode.length,
    previous,
    next,
    appointmentsLeft: inEpisode.length - (index + 1),
  };
}

/**
 * Load patient appointments and resolve EoC-scoped sequence / prev / next.
 */
export async function findEpisodeLinkedAppointments(
  medplum: Pick<MedplumClient, 'searchResources' | 'invalidateSearches'>,
  patientId: string,
  currentAppointmentId: string,
  episodeOfCareId: string | undefined,
): Promise<EpisodeLinkedAppointmentsResult> {
  if (!episodeOfCareId) {
    return {
      sequenceNumber: null,
      totalInEpisode: 0,
      appointmentsLeft: null,
    };
  }

  try {
    medplum.invalidateSearches('Appointment');
    const results = await medplum.searchResources('Appointment', {
      patient: `Patient/${patientId}`,
      _count: '200',
      _sort: 'date',
    });
    return resolveEpisodeLinkedAppointments(results, currentAppointmentId, episodeOfCareId);
  } catch {
    return {
      sequenceNumber: null,
      totalInEpisode: 0,
      appointmentsLeft: null,
    };
  }
}
